import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';
import Select from 'react-select';
import chroma from 'chroma-js';
import { Loader } from 'components/Loaders';

const customStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: 'white',
    borderColor: '#ccc',
    boxShadow: 'none',
    minHeight: 40,
    ':hover': {
      borderColor: '#999',
    },
  }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    const color = chroma(data.color || '#ccc');
    return {
      ...styles,
      backgroundColor: isDisabled
        ? undefined
        : isSelected
          ? data.color
          : isFocused
            ? color.alpha(0.1).css()
            : undefined,
      color: '#000',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      ':active': {
        ...styles[':active'],
        backgroundColor: !isDisabled
          ? isSelected
            ? data.color
            : color.alpha(0.3).css()
          : undefined,
      },
    };
  },
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: chroma(data.color || '#ccc').alpha(0.1).css(),
    fontFamily: '\'Roboto\', \'Helvetica\', \'Arial\', sans-serif',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
    color: '#666 !important',
  }),
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: data.color || '#333',
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: data.color || '#333',
    ':hover': {
      backgroundColor: data.color,
      color: chroma.contrast(data.color, 'white') > 4.5 ? 'white' : 'black',
    },
  }),
  placeholder: (styles) => ({
    ...styles,
    fontFamily: '\'Roboto\', \'Helvetica\', \'Arial\', sans-serif',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
    color: '#666 !important',
  }),
};

function MultiSelectTagControl({
  settings = {},
  placeholder = 'Seleccionar...',
  events = {},
  className,
  value = [],
  loading = false,
  onChange,
  name,
}) {
  const { options = [] } = settings;

  const processedOptions = useMemo(() =>
    options.map((option) => ({
      value: option.id,
      label: option.name,
      color: option.color || '#7055AF',
      description: option.description,
    })),
  [options]);

  const selectedValues = useMemo(() =>
    processedOptions.filter((option) =>
      value.includes(option.value),
    ),
  [value, processedOptions]);

  const handleChange = (selected) => {
    const newValues = selected ? selected.map((item) => item.value) : [];

    onChange({
      target: {
        name,
        value: newValues,
      },
    });
    if (events.onChange) {
      events.onChange({
        name,
        value: newValues,
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <Loader size={24} />
      </Box>
    );
  }

  return (
    <Box className={className}>
      <Select
        isMulti
        inputId="multi-select-tag"
        aria-label={placeholder}
        value={selectedValues}
        placeholder={placeholder}
        options={processedOptions}
        closeMenuOnSelect={false}
        styles={{
          ...customStyles,
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        onChange={handleChange}
        formatOptionLabel={(option, { context }) => (
          context === 'value'
            ? option.label
            : (
              <>
                <Typography variant='body1' style={{
                  fontSize: 'bold',
                }}>{option.label}</Typography>
                {option.description && (
                  <Typography variant='body2' style={{
                    color: '#666',
                  }}>
                    {option.description}
                  </Typography>
                )}
              </>
            )
        )}
      />
    </Box>
  );
}

MultiSelectTagControl.propTypes = {
  settings: PropTypes.shape({
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        color: PropTypes.string,
        description: PropTypes.string,
      }),
    ),
  }),
  placeholder: PropTypes.string,
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ).isRequired,
  className: PropTypes.string,
  loading: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  events: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
  }),
};

export default MultiSelectTagControl;
